'use client'
import Link from 'next/link'
import ImageComponent from '../../common/ImageComponent'
import { ArrowUpRightSVG } from '@/src/ui/AllSVG'
import { useGSAP } from '@gsap/react'
import { useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
interface SeasonalBannerProps {
    video?: string
    image?: string
    season?: string
    heading?: { normal: string; italic: string }
    description?: string
    cta?: { label: string; href: string }
}
gsap.registerPlugin(ScrollTrigger)

export default function SeasonalBanner({
    video,
    image,
    season,
    heading = { normal: 'Built for the', italic: 'Cold.' },
    description = 'Structured outerwear and disciplined layers designed for movement through the city.',
    cta = { label: 'SHOP COLLECTION', href: '/shop' },
}: SeasonalBannerProps) {


    const cardRef = useRef<HTMLDivElement>(null)
    const textRef = useRef<HTMLDivElement>(null)
    const bgRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        if (!cardRef.current || !textRef.current) return

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: cardRef.current,
                start: 'top 40%',
                // end: 'bottom center',
                toggleActions: 'play none restart reverse',
                // markers: true,
            },
        })


        // Season label — only animate when the element is actually rendered
        if (season) {
            tl.from(textRef.current.querySelectorAll('.sb-label'), {
                opacity: 0,
                y: 16,
                duration: 0.5,
                ease: 'power2.out',
            })
        }
        // Heading — text reveal from clip
        tl.from(textRef.current.querySelectorAll('.sb-reveal'), {
            yPercent: 100,
            duration: 0.7,
            stagger: 0.12,
            ease: 'power3.out',
        }, '-=0.2')
        // Description + CTA — fade up
        tl.from(textRef.current.querySelectorAll('.sb-fade'), {
            opacity: 0,
            y: 20,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
        }, '-=0.3')
    }, { scope: cardRef, dependencies: [season] })

    return (
        <>
            <div ref={cardRef} className="relative w-full h-[60vh] md:h-[90vh] overflow-hidden">
                {/* Background — video or image */}
                <div ref={bgRef} className="absolute inset-0 will-change-transform">
                    {video ? (
                        <video
                            src={video}
                            autoPlay
                            muted
                            loop
                            playsInline
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <ImageComponent
                            src={image}
                            alt={`${heading?.normal} ${heading?.italic}`}
                            object_cover
                            priority={false}
                        />
                    )}
                </div>
                {/* Overlay */}
                <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent" />
                {/* Content — bottom left */}
                <div className={`text-white absolute inset-0 flex flex-col custom_container ${video ? 'justify-end pb-8 md:pb-10' : 'justify-center'}`}>
                    {/* Animated text block */}
                    <div ref={textRef}>
                        {season && (
                            <p className="sb-label text-xs font-semibold tracking-widest uppercase mb-2">{season}</p>
                        )}
                        <h2 className="secondary_heading font-medium!">
                            <span className="overflow-hidden inline-block">
                                <span className="sb-reveal inline-block">{heading?.normal}</span>
                            </span>{' '}
                            <span className="overflow-hidden inline-block">
                                <em className="sb-reveal inline-block font-secondary">{heading?.italic}</em>
                            </span>
                        </h2>
                        <p className="sb-fade text-sm max-w-sm mb-5">{description}</p>
                        <Link href={cta?.href || ''} className="sb-fade underline_decoration">
                            {cta?.label} <ArrowUpRightSVG />
                        </Link>
                    </div>
                </div>
            </div>
        </>
    )
}
